import { useWorkspacesPageMemberQuery } from '../../../generated';
import type { DirectoryMember } from '../types';
import { mapToMember } from '../utils';

export interface UseWorkspaceMemberResult {
  /** Null until the read comes back, and whenever it did not find a member. */
  member: DirectoryMember | null;
  isLoading: boolean;
  error: Error | null;
  /** The workspace did not resolve, rather than the member being absent. */
  notFound: boolean;
  /** The workspace resolved; this user holds no membership in it. */
  notAMember: boolean;
}

/**
 * One member of a workspace, by user id.
 *
 * The directory read already lists every member of the workspace, so this
 * answers for whichever of them the reader picked — including someone the
 * caller shares no location with, which the shared read cannot show.
 *
 * Pass a null userId for a closed panel: the read is skipped rather than asked
 * a question with no subject.
 */
export const useWorkspaceMember = (
  workspaceId: string,
  userId: string | null
): UseWorkspaceMemberResult => {
  const member = useWorkspacesPageMemberQuery({
    variables: { input: { workspaceId, userId: userId ?? '' } },
    skip: !userId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  // A new subject is a cache miss, so `data` is undefined until the read
  // returns. Falling back to the last result would show the previous member
  // under the new name, so the panel waits instead.
  const result = member.data?.teamMemberGet;

  const notFound = result?.__typename === 'WorkspaceNotFound';
  const notAMember = result?.__typename === 'TeamMemberNotFound';

  const resultError =
    result?.__typename === 'AuthorizationFailure' ||
    result?.__typename === 'InputValidationFailure'
      ? new Error(result.message ?? 'The member could not be read.')
      : null;

  return {
    member:
      result?.__typename === 'TeamMemberGetResult'
        ? mapToMember(result.teamMember)
        : null,
    isLoading: member.loading,
    error: member.error ?? resultError,
    notFound,
    notAMember,
  };
};
